import React from "react";
import {
    Dialog,
    DialogTrigger,
    Form,
    GridList,
    GridListItem,
    Modal,
    ModalOverlay,
    VisuallyHidden,
} from "react-aria-components";
import { msgid, ngettext, t } from "ttag";

import type { ArticlePageTile, FilteredListBlock } from "@reactivated";
import { constants } from "@reactivated";

import {
    ButtonTertiary,
    ButtonTextSecondary,
    Clickable,
    LongButton,
} from "../Clickables";
import { Radio, RadioGroup } from "../Form";
import { H, HLevel } from "../Headings";
import { Separator } from "../Separator";
import { Svg } from "../Svg";
import { TileImage } from "../TileImage";

import styles from "./FilterableList.module.scss";

type ArticleTagLink = ArticlePageTile["tags"][number];
const groups = constants["server.cms.types.TagGroups"];
export const ALL_VALUE = "__all__";

export const Filters = (props: {
    categories: (ArticleTagLink & { isDisabled?: boolean })[];
    values: Record<string, string>;
    onValuesChange: (group: string, value: string) => void;
    className?: string;
}) => {
    const filterGroups = [
        { group: groups.TYPE, label: t`Content`, allLabel: t`All Content` },
        { group: groups.TOPIC, label: t`Topics`, allLabel: t`All Topics` },
        {
            group: groups.AUDIENCE,
            label: t`This is for`,
            allLabel: t`For Everyone`,
        },
    ] as const;

    return (
        <Form className={props.className}>
            {filterGroups.map(({ group, label, allLabel }) => {
                const cats = props.categories.filter(
                    (cat) => cat.category === group,
                );
                if (!cats.length) return null;
                return (
                    <RadioGroup
                        key={group}
                        name={group}
                        value={props.values[group]}
                        onChange={(v) => props.onValuesChange(group, v)}
                        label={label}
                    >
                        <Radio value={ALL_VALUE}>{allLabel}</Radio>
                        {cats.map((cat) => (
                            <Radio
                                key={cat.url}
                                value={cat.value}
                                isDisabled={cat.isDisabled ?? false}
                            >
                                {cat.display_name}
                            </Radio>
                        ))}
                    </RadioGroup>
                );
            })}
        </Form>
    );
};

export const List = (
    props: React.PropsWithChildren<{
        items: ArticlePageTile[];
        featured?: FilteredListBlock["featured"];
    }>,
) => (
    <HLevel>
        <GridList
            className={styles.root}
            layout="stack"
            selectionMode="none"
            aria-label={t`Filtered results`}
        >
            {props.featured && (
                <GridListItem
                    id="featured"
                    textValue={props.featured.title}
                    className={styles.featured}
                >
                    <div className={styles.itemText}>
                        <p className={styles.eyebrow}>
                            {props.featured.category}
                        </p>
                        <H className={styles.featuredTitle}>
                            {props.featured.title}
                        </H>
                        {props.featured.search_description?.trim() && (
                            <p className={styles.sdesc}>
                                {props.featured.search_description}
                            </p>
                        )}
                    </div>
                    <TileImage
                        value={props.featured}
                        attrs={{
                            "aria-hidden": true,
                            "className": styles.tileImage,
                        }}
                    />
                    <LongButton variant="primary" href={props.featured.url}>
                        {t`Read ${props.featured.title}`}
                    </LongButton>
                </GridListItem>
            )}
            {props.items.map((item) => (
                <GridListItem
                    key={item.id}
                    id={item.id}
                    textValue={item.title}
                    className={styles.item}
                    href={item.url}
                >
                    <TileImage
                        value={item}
                        attrs={{
                            "aria-hidden": true,
                            "className": styles.tileImage,
                        }}
                    />
                    <div className={styles.itemText}>
                        <p className={styles.eyebrow}>{item.category}</p>
                        <H className={styles.title}>{item.title}</H>
                        {item.search_description?.trim() && (
                            <p className={styles.sdesc}>
                                {item.search_description}
                            </p>
                        )}
                    </div>
                </GridListItem>
            ))}
            {props.children}
        </GridList>
    </HLevel>
);

const unfiltered = {
    [groups.TYPE]: ALL_VALUE,
    [groups.TOPIC]: ALL_VALUE,
    [groups.AUDIENCE]: ALL_VALUE,
} as const;

export const FilterableList = (props: {
    items: ArticlePageTile[];
    categories: ArticleTagLink[];
    value: FilteredListBlock;
}) => {
    const [filterValues, setFilterValues] =
        React.useState<Record<string, string>>(unfiltered);

    const items = React.useMemo(
        () =>
            props.items.filter((item) =>
                Object.entries(filterValues).every(
                    ([grp, val]) =>
                        val === ALL_VALUE ||
                        item.tags.some(
                            (tag) => tag.category === grp && tag.value === val,
                        ),
                ),
            ),
        [props.items, filterValues],
    );

    const categoriesWithCounts = props.categories.map((cat) => ({
        ...cat,
        isDisabled:
            props.items.filter((item) =>
                Object.entries({
                    ...filterValues,
                    [cat.category]: cat.value,
                }).every(
                    ([grp, val]) =>
                        val === ALL_VALUE ||
                        item.tags.some(
                            (tag) => tag.category === grp && tag.value === val,
                        ),
                ),
            ).length === 0,
    }));

    const handleValuesChange = (group: string, value: string) => {
        setFilterValues((prev) => ({ ...prev, [group]: value }));
    };

    return (
        <>
            <div className={styles.filters}>
                <DialogTrigger>
                    <div className={styles.filterTop}>
                        <Clickable className={styles.mobileTrigger}>
                            {t`Filter`}
                            <Svg name="filter" aria-hidden />
                        </Clickable>
                        <span className={styles.resultCount}>
                            {ngettext(
                                msgid`${items.length} Result`,
                                `${items.length} Results`,
                                items.length,
                            )}
                        </span>
                    </div>
                    <ModalOverlay className={styles.modalOverlay} isDismissable>
                        <Modal className={styles.modal}>
                            <Dialog
                                className={styles.dialog}
                                aria-label={t`Filters`}
                            >
                                {({ close }) => (
                                    <>
                                        <Clickable
                                            className={styles.closeButton}
                                            onClick={close}
                                        >
                                            <span aria-hidden>
                                                {t`Filter`}{" "}
                                                <Svg name="filter" />
                                            </span>
                                            <span>
                                                <VisuallyHidden>
                                                    {t`Close`}
                                                </VisuallyHidden>
                                                <Svg name="close" aria-hidden />
                                            </span>
                                        </Clickable>
                                        <Separator orientation="horizontal" />
                                        <Filters
                                            categories={categoriesWithCounts}
                                            values={filterValues}
                                            onValuesChange={handleValuesChange}
                                            className={styles.modalFilters}
                                        />
                                        <ButtonTertiary onClick={close}>
                                            {t`Show Results`}
                                        </ButtonTertiary>
                                        <ButtonTextSecondary
                                            onClick={() => {
                                                setFilterValues(unfiltered);
                                                close();
                                            }}
                                        >
                                            {t`Clear All`}
                                        </ButtonTextSecondary>
                                    </>
                                )}
                            </Dialog>
                        </Modal>
                    </ModalOverlay>
                </DialogTrigger>
                <Filters
                    categories={categoriesWithCounts}
                    values={filterValues}
                    onValuesChange={handleValuesChange}
                    className={styles.desktop}
                />
            </div>
            <List items={items} featured={props.value.featured} />
        </>
    );
};
